// Вклад разделов в общий прогресс капсулы: анкета весит половину,
// материалы и заметки — по четверти. Значения из макета, на бэке их нет
export const CAPSULE_SECTION_WEIGHT = {
    questionnaire: 50,
    files: 25,
    notes: 25
};

// forms: [для 1, для 2-4, для 5-0] — «1 заметка», «2 заметки», «5 заметок»
export const pluralHandler = (count, forms) => {
    const value = Math.abs(Number(count) || 0) % 100;
    const tail = value % 10;

    if (value > 10 && value < 20) {
        return forms[2];
    }
    if (tail > 1 && tail < 5) {
        return forms[1];
    }
    if (tail === 1) {
        return forms[0];
    }
    return forms[2];
};

// Общий процент капсулы. С API 19 его считает сервер (stats.readiness.percent) —
// веса и цель по объёму файлов настраиваются в админке, поэтому свой расчёт
// держим только как запасной для старых ответов
export const capsulePercentHandler = (stats) => {
    if (!stats) {
        return 0;
    }

    const readiness = stats?.readiness?.percent;
    if (readiness !== undefined && readiness !== null && readiness !== '') {
        return Math.min(Math.round(Number(readiness) || 0), 100);
    }

    const questionnaire = Math.min(stats?.questionnaire?.percent || 0, 100);
    const files = Math.min(stats?.files?.percent || 0, 100);
    const notes = Math.min(stats?.notes?.percent || 0, 100);

    return Math.round(
        (questionnaire * CAPSULE_SECTION_WEIGHT.questionnaire
            + files * CAPSULE_SECTION_WEIGHT.files
            + notes * CAPSULE_SECTION_WEIGHT.notes) / 100
    );
};

// Сколько баллов из своего веса набрал раздел: с API 19 приходит в
// readiness.parts, иначе считаем по проценту раздела
export const capsuleSectionPointsHandler = (stats, key) => {
    const weight = CAPSULE_SECTION_WEIGHT[key] || 0;
    const part = stats?.readiness?.parts?.[key];

    if (part !== undefined && part !== null && part !== '') {
        return Math.min(Math.round(Number(part) || 0), weight);
    }

    const percent = Math.min(stats?.[key]?.percent || 0, 100);
    return Math.round((weight * percent) / 100);
};

// Капсулу можно редактировать только в статусе open
export const isCapsuleEditable = (capsuleStatus) => capsuleStatus === 'open';

// Капсула не куплена: активна только покупка, остальные разделы закрыты.
// false пока статус не приехал — иначе страница успевает моргнуть замком
export const isCapsuleLocked = (capsuleStatus) => capsuleStatus === 'locked';

// Капсула запечатана: содержимое зафиксировано. Только с этого момента есть из
// чего собирать цифровую копию, поэтому аватар заводится после запечатывания
export const isCapsuleSealed = (capsuleStatus) => capsuleStatus === 'sealed';

// Блок анкеты пройден, когда на каждый вопрос уже отреагировали: ответили или
// пропустили. Пустой ответ бэк принимает как пропуск, поэтому answered + skipped
// доходит до total сразу после сохранения блока целиком
export const isQuestionBlockDone = (block) => {
    const total = Number(block?.total) || 0;
    if (!total) {
        return false;
    }
    return (Number(block?.answered) || 0) + (Number(block?.skipped) || 0) >= total;
};

// Первый непройденный блок: с него открываем анкету. -1, если пройдены все
export const firstOpenBlockIndex = (blocks) => {
    if (!Array.isArray(blocks)) {
        return -1;
    }
    return blocks.findIndex(block => !isQuestionBlockDone(block));
};

// KB/MB считаем по 1000, а не по 1024: так размер совпадает с тем, что человек
// видит в проводнике и в Finder. По 1024 вышло бы 111 KB там, где система
// показывает 113, и файл выглядел бы «не тем»
export const fileSizeHandler = (bytes) => {
    const value = Number(bytes) || 0;

    if (value >= 1000 * 1000 * 1000) {
        return `${(value / (1000 * 1000 * 1000)).toFixed(1)} GB`;
    }
    if (value >= 1000 * 1000) {
        return `${(value / (1000 * 1000)).toFixed(1)} MB`;
    }
    if (value >= 1000) {
        return `${Math.round(value / 1000)} KB`;
    }
    return `${value} B`;
};

// Квота хранилища задана на сервере в двоичных единицах (1 GB = 1073741824),
// поэтому её и занятое место считаем по 1024 — иначе «1 GB» превращается в
// «1.1 GB». Размер отдельного файла по-прежнему десятичный, как в проводнике
export const storageSizeHandler = (bytes) => {
    const value = Number(bytes) || 0;

    if (value >= 1024 * 1024 * 1024) {
        const gb = value / (1024 * 1024 * 1024);
        return `${Number(gb.toFixed(gb < 10 ? 1 : 0))} GB`;
    }
    if (value >= 1024 * 1024) {
        const mb = value / (1024 * 1024);
        return `${Number(mb.toFixed(mb < 10 ? 1 : 0))} MB`;
    }
    if (value >= 1024) {
        return `${Math.round(value / 1024)} KB`;
    }
    return `${value} B`;
};

// С API 16 файлы ограничены не количеством, а объёмом: сервер отдаёт квоту
// и занятое место и в /capsule/files, и в /capsule/stats — берём первое, что есть
export const storageHandler = (filesStorage, statsStorage) => {
    const source = filesStorage || statsStorage || null;

    const quota = Number(source?.quota_bytes) || 0;
    const used = Number(source?.used_bytes) || 0;
    const free = source?.free_bytes !== undefined
        ? Math.max(Number(source.free_bytes) || 0, 0)
        : Math.max(quota - used, 0);

    return {
        quota,
        used,
        free,
        percent: Math.min(Number(source?.percent) || (quota ? Math.round((used / quota) * 100) : 0), 100),
        // пока квота не приехала, не блокируем загрузку — проверит сервер
        isFull: !!quota && free <= 0,
        known: !!source
    };
};

export const noteDateHandler = (value) => {
    if (!value) {
        return '';
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
        return '';
    }

    const pad = (num) => String(num).padStart(2, '0');
    return `${pad(date.getDate())}.${pad(date.getMonth() + 1)}.${date.getFullYear()}`;
};

// Первая строка заметки становится её заголовком в списке
export const noteTitleHandler = (text) => {
    const first = String(text || '').split('\n').find(line => line.trim().length);
    return first ? first.trim() : '';
};

export const noteExcerptHandler = (text) => {
    const lines = String(text || '').split('\n').map(line => line.trim()).filter(line => line.length);
    return lines.slice(1).join(' ');
};

export const dateTimeHandler = (value) => {
    if (!value) {
        return '';
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
        return '';
    }

    const pad = (num) => String(num).padStart(2, '0');
    return `${pad(date.getDate())}.${pad(date.getMonth() + 1)}.${date.getFullYear()}, ${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

// Баланс и цена приходят строкой с двумя знаками: хвостовые нули не показываем
export const amountHandler = (value) => {
    const num = Number(value);
    if (Number.isNaN(num)) {
        return '0';
    }
    return String(Number(num.toFixed(2)));
};
